import { GiftCardType } from "@/global/store/data/types";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";
import { EmptyState } from "@/shared/base/components/EmptyState/EmptyState";
import { IosSwitch } from "@/shared/base/components/IosSwitch/IosSwitch";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
  useThemeProps,
} from "@mui/material";
import React from "react";
import { CgGift } from "react-icons/cg";
import { CgProfile } from "react-icons/cg";
import {
  Email as EmailInput,
  Message as MessageInput,
} from "@/shared/base/components/FormComponents/FormComponents";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { RecipientDetailsType } from "./usePurchaseGiftCard";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { createPortal } from "react-dom";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { HStackListItem } from "@/shared/base/components/HStackListItem";
import { useTranslation } from "react-i18next";

interface GiftCardAmountsProps<T = GiftCardType> {
  isLoading?: boolean;
  giftCards: T[];
  selectedGiftCard: T | null;
  onSelectGiftCard: (giftCard: T) => void;
}
export function GiftCardAmounts(props: GiftCardAmountsProps) {
  const theme = useTheme();
  const { sm: smDown } = useCssMediaQuery("down");
  const { languages } = useTeamConfig();
  const { t } = useTranslation();

  return (
    <Box border={`1px solid ${theme.palette.divider}`} borderRadius={0.9}>
      <Stack
        direction={"row"}
        justifyContent={"flex-start"}
        alignItems={"center"}
        px={2}
        py={2.5}
        spacing={1}
      >
        <CgGift fontSize={"1.6rem"} />
        <Typography
          variant="subtitle2"
          fontWeight={"700"}
          lineHeight={1.3}
          color="text.primary"
        >
          {t("giftCard.select.amountTitle") ||
            languages?.purchaseGiftCard?.amountTitle}
        </Typography>
      </Stack>
      <Divider />
      <Box p={3}>
        {props.isLoading ? (
          <Box width={"100%"}>
            <DynamicSkeleton
              rowCount={4}
              heights={["16px", "16px", "16px", "16px"]}
              widths={["100%", "75%", "50%", "25%"]}
              spacing={2}
            />
          </Box>
        ) : null}

        {!props.isLoading && props.giftCards.length > 0 ? (
          <Stack direction={"row"} flexWrap={"wrap"} rowGap={2} columnGap={2}>
            {props.giftCards.map((itm) => {
              const isSelected = props?.selectedGiftCard?.id == itm.id;
              return (
                <Button
                  key={itm.id}
                  onClick={() => props.onSelectGiftCard(itm)}
                  variant={isSelected ? "contained" : "outlined"}
                  size={smDown ? "medium" : "large"}
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                >
                  ${Number(itm.amount)}
                </Button>
              );
            })}{" "}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}

interface RecipientDetailsProps {
  formMethods: ReturnType<typeof useForm>;
  onSubmit: (data: any) => void;
}

export const RecipientDetails = React.forwardRef(function RecipientDetails(
  props: RecipientDetailsProps,
  ref: React.Ref<HTMLButtonElement>
) {
  const { t } = useTranslation();
  const formMethods = props?.formMethods;
  const { languages } = useTeamConfig();
  const theme = useTheme();
  const isRecipientEnabled = "isRecipientEnabled";

  const { watch, control, handleSubmit } = props.formMethods;
  const recipientEnabled = watch(isRecipientEnabled, true);

  const onChangeEmail = (event: any) => {
    const email = event?.target?.value;
    formMethods.setValue("email", email);
    const hasError = !!formMethods.formState.errors.email?.message;
    if (hasError) formMethods.trigger("email");
  };

  return (
    <FormProvider {...props.formMethods}>
      <form onSubmit={handleSubmit(() => {})}>
        <Box
          border={`1px solid ${theme.palette.divider}`}
          borderRadius={0.9}
          mb={3}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            px={2}
            py={2.5}
          >
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"flex-start"}
              spacing={1}
            >
              <CgProfile fontSize={"1.6rem"} />
              <Typography
                variant="subtitle2"
                fontWeight={"700"}
                lineHeight={1.3}
                color="text.primary"
              >
                {t("giftCard.checkout.recipientTitle") ||
                  languages?.purchaseGiftCard?.recipientTitle}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Box p={3} pb={1}>
            <Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={400}
                  color="text.secondary"
                >
                  {t("giftCard.checkout.recipientOnDescription") ||
                    languages?.purchaseGiftCard?.recipientEnabledDesc}
                </Typography>
              </Box>
              <Box mt={2}>
                <Stack flexGrow={1}>
                  <TextField
                    {...props?.formMethods?.register("email", {
                      required:
                        t("giftCard.checkout.recipientEmailRequiredText") ||
                        "Email is Required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Invalid email format",
                      },
                    })}
                    onChange={onChangeEmail}
                    fullWidth
                    type="email"
                    placeholder={
                      t("giftCard.checkout.recipientEmailInputPlaceholder") ||
                      "Email"
                    }
                    size="small"
                  />
                  <Typography
                    color="error.main"
                    ml="8px"
                    fontSize="12px"
                    minHeight="18px"
                  >
                    {
                      props?.formMethods?.formState?.errors?.email
                        ?.message as string
                    }
                  </Typography>
                </Stack>
              </Box>
              <Box mt={1}>
                <MessageInput
                  placeholder={
                    t("giftCard.checkout.recipientMessageInputPlaceholder") ||
                    "Message"
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* hidden submit btn */}
        <button ref={ref} style={{ display: "none", width: 0, height: 0 }} />
      </form>
    </FormProvider>
  );
});

export function GiftCardBillingSummary(props: {
  selectedGiftCard: GiftCardType | null;
}) {
  const theme = useTheme();
  const { selectedGiftCard } = props;
  const { t } = useTranslation();

  return (
    <Box>
      <Typography variant="h6" color={"text.primary"} fontWeight={700}>
        {t("giftCard.checkout.billingSummaryTitle") || "Your Items"}
      </Typography>
      <Box
        border={`1px solid ${theme.palette.divider}`}
        mt={1}
        borderRadius={0.8}
      >
        {selectedGiftCard ? (
          <Stack spacing={1} p={2}>
            <Typography
              variant="subtitle1"
              color={"text.secondary"}
              fontWeight={500}
            >
              {t("giftCard.checkout.billingAmountLabel") || "Gift Card Amount"}
            </Typography>
            <Typography variant="h3" color="text.primary" fontWeight={400}>
              {getUSCurrency(Number(selectedGiftCard.amount))}
            </Typography>
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}

export function GiftCardBillingSubtotal(props: {
  selectedGiftCard: GiftCardType;
}) {
  const giftCardAmount = Number(props.selectedGiftCard.amount);
  const serviceFee = Number(props?.selectedGiftCard?.serviceFee) || 0;
  const totalAmount = giftCardAmount + serviceFee;
  const { t } = useTranslation();

  const TotalDue = () => (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <Typography
        variant="subtitle1"
        fontSize={"1.4rem"}
        color="text.primary"
        fontWeight={700}
      >
        {t("giftCard.checkout.billingTotalLabel") || "Total Due"}
      </Typography>
      <Typography variant="h3" color="text.primary" fontWeight={600}>
        {getUSCurrency(totalAmount)}
      </Typography>
    </Stack>
  );

  if (serviceFee > 0) {
    return (
      <Stack>
        <Divider />
        <Stack p={3}>
          <HStackListItem
            label={
              t("giftCard.checkout.billingAmountLabel") || "Gift Card Amount"
            }
            value={getUSCurrency(Number(props.selectedGiftCard.amount))}
          />
          <HStackListItem
            label={
              t("giftCard.checkout.billingServiceFeeLabel") || "Service Fee"
            }
            value={getUSCurrency(serviceFee)}
          />
          <TotalDue />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack>
      <Divider />
      <Stack p={3}>
        <TotalDue />
      </Stack>
    </Stack>
  );
}

export function GiftCardCheckoutModal(props: { children: React.ReactNode }) {
  const theme = useTheme();
  const { md: mdDown } = useCssMediaQuery("down");

  return createPortal(
    <Box
      width={"100%"}
      height={"100vh"}
      top="0"
      left="0"
      position={"fixed"}
      zIndex={"999"}
      bgcolor={"rgba(0,0,0,0.5)"}
      sx={{ backdropFilter: "blur(5px)" }}
      p={mdDown ? 0 : 8}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      <Box
        width={mdDown ? "100%" : "1200px"}
        height={mdDown ? "100vh" : "750px"}
        bgcolor={theme.palette.background.paper}
        borderRadius={mdDown ? 0 : 0.8}
      >
        {props.children}
      </Box>
    </Box>,
    document.body
  );
}
